// Builds the Mapbox paint-property expressions the print-maps preview
// uses for its route + border line layers, sourced from the user's
// cloud-synced route style settings.
//
// The settings flow:
//   server (User.route_style_settings JSONB)
//     → routeStyleSettingsCloudSync.hydrateFromServer (boot + login)
//     → localStorage[ROUTE_STYLE_STORAGE_KEY]
//     → getRouteStyleSettings() in this module
//     → Mapbox `case`-on-`routeStyle` expressions
//
// Mirrors the Ruby resolver `PrintMapRouteStyleResolver` (which the
// backend uses when rendering the final PDF) so the preview line on
// /print_maps/new and the line on the generated PDF render with the
// same colour, width, and opacity. The base widths (`line` = 5,
// `border` = 2) are the same the print preview used historically;
// the user's `thickness` multiplier scales both.
//
// Pure helper — no DOM / no Mapbox runtime dependency — so it's
// exercised directly from Jest.

import {
  DEFAULT_ROUTE_STYLE_SETTINGS,
  getRouteStyleSettings,
  RouteStyleSettings,
} from './routeStyleSettings';

// Historical preview widths. Kept in lock-step with the values
// `PrintMapModule.ts` used before this change so users without
// `route_style_settings` see no visual change.
export const PREVIEW_LINE_BASE_WIDTH = 5;
export const PREVIEW_BORDER_BASE_WIDTH = 2;
export const PREVIEW_BORDER_COLOR_FALLBACK = '#DB2B35';

export interface PrintPreviewPaintExpressions {
  // Mapbox `case`-on-routeStyle expressions, ready to drop into the
  // `paint` object of a `line` layer.
  lineColor: unknown[];
  lineWidth: unknown[];
  lineOpacity: unknown[];
  borderColor: unknown[];
  borderWidth: unknown[];
}

/**
 * Reads the user's current settings from localStorage and builds paint
 * expressions for the preview's `-line` and `-border` layers.
 */
export function buildPrintPreviewPaintExpressions(): PrintPreviewPaintExpressions {
  return buildExpressions(getRouteStyleSettings());
}

/**
 * Pure overload that lets tests inject settings directly (no
 * localStorage). Same logic, no side effects.
 */
export function buildExpressions(settings: RouteStyleSettings): PrintPreviewPaintExpressions {
  // Web stores 4 tabs (main / alt / spur / recording) plus a global
  // hideMileMarkers boolean. Mapbox `case` arms match `properties.routeStyle`
  // which the backend emits as `route` / `alt` / `spur` / `recording` /
  // `transport`. The `transport` arm inherits `main` (matches the
  // browser hike-map's behaviour — there's no separate "transport"
  // tab in the UI).
  const s = settings;

  return {
    lineColor: [
      'case',
      ['==', ['get', 'routeStyle'], 'route'],     s.main.color,
      ['==', ['get', 'routeStyle'], 'alt'],        s.alt.color,
      ['==', ['get', 'routeStyle'], 'spur'],       s.spur.color,
      ['==', ['get', 'routeStyle'], 'recording'],  s.recording.color,
      ['==', ['get', 'routeStyle'], 'transport'],  s.main.color,
      s.main.color,
    ],
    lineWidth: [
      'case',
      ['==', ['get', 'routeStyle'], 'route'],     PREVIEW_LINE_BASE_WIDTH * s.main.thickness,
      ['==', ['get', 'routeStyle'], 'alt'],        PREVIEW_LINE_BASE_WIDTH * s.alt.thickness,
      ['==', ['get', 'routeStyle'], 'spur'],       PREVIEW_LINE_BASE_WIDTH * s.spur.thickness,
      ['==', ['get', 'routeStyle'], 'recording'],  PREVIEW_LINE_BASE_WIDTH * s.recording.thickness,
      ['==', ['get', 'routeStyle'], 'transport'],  PREVIEW_LINE_BASE_WIDTH * s.main.thickness,
      PREVIEW_LINE_BASE_WIDTH,
    ],
    lineOpacity: [
      'case',
      ['==', ['get', 'routeStyle'], 'route'],     s.main.opacity / 100,
      ['==', ['get', 'routeStyle'], 'alt'],        s.alt.opacity / 100,
      ['==', ['get', 'routeStyle'], 'spur'],       s.spur.opacity / 100,
      ['==', ['get', 'routeStyle'], 'recording'],  s.recording.opacity / 100,
      ['==', ['get', 'routeStyle'], 'transport'],  s.main.opacity / 100,
      1.0,
    ],
    borderColor: [
      'case',
      ['==', ['get', 'routeStyle'], 'route'],     s.main.color,
      ['==', ['get', 'routeStyle'], 'alt'],        s.alt.color,
      ['==', ['get', 'routeStyle'], 'spur'],       s.spur.color,
      ['==', ['get', 'routeStyle'], 'recording'],  s.recording.color,
      ['==', ['get', 'routeStyle'], 'transport'],  s.main.color,
      PREVIEW_BORDER_COLOR_FALLBACK,
    ],
    borderWidth: [
      'case',
      ['==', ['get', 'routeStyle'], 'route'],     PREVIEW_BORDER_BASE_WIDTH * s.main.thickness,
      ['==', ['get', 'routeStyle'], 'alt'],        PREVIEW_BORDER_BASE_WIDTH * s.alt.thickness,
      ['==', ['get', 'routeStyle'], 'spur'],       PREVIEW_BORDER_BASE_WIDTH * s.spur.thickness,
      ['==', ['get', 'routeStyle'], 'recording'],  PREVIEW_BORDER_BASE_WIDTH * s.recording.thickness,
      ['==', ['get', 'routeStyle'], 'transport'],  PREVIEW_BORDER_BASE_WIDTH * s.main.thickness,
      PREVIEW_BORDER_BASE_WIDTH,
    ],
  };
}

// Re-export for test convenience.
export { DEFAULT_ROUTE_STYLE_SETTINGS };
